import React from 'react';
import { Table, Lock, Edit, Eye, ChevronRight } from 'lucide-react';

const TableBrowser = ({ tables, onSelectTable }) => {
  const getAccessIcon = (access) => {
    switch (access) {
      case 'readonly':
        return <Eye className="h-4 w-4 text-gray-500" />;
      case 'update':
        return <Edit className="h-4 w-4 text-blue-500" />;
      case 'full':
        return <Edit className="h-4 w-4 text-green-500" />;
      default:
        return <Lock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getAccessBadge = (access) => {
    const badges = {
      readonly: 'bg-gray-100 text-gray-700 border-gray-300',
      update: 'bg-blue-100 text-blue-700 border-blue-300',
      full: 'bg-green-100 text-green-700 border-green-300'
    };

    const labels = {
      readonly: 'Read Only',
      update: 'Update',
      full: 'Full Access'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badges[access]}`}>
        {getAccessIcon(access)}
        <span className="ml-1">{labels[access]}</span>
      </span>
    );
  };

  // Group tables by access level
  const groupedTables = {
    full: tables.filter(t => t.access === 'full'),
    update: tables.filter(t => t.access === 'update'),
    readonly: tables.filter(t => t.access === 'readonly')
  };

  const renderTableGroup = (title, tableList, bgColor) => {
    if (tableList.length === 0) return null;

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          {title}
          <span className="ml-2 text-sm font-normal text-gray-500">({tableList.length} tables)</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tableList.map(table => (
            <button
              key={table.name}
              onClick={() => onSelectTable(table)}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all text-left group"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center">
                  <Table className="h-5 w-5 text-gray-400 mr-2" />
                  <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600">
                    {table.displayName}
                  </h4>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
              </div>

              <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                {table.description}
              </p>

              <div className="flex items-center justify-between">
                {getAccessBadge(table.access)}
                <span className="text-xs text-gray-500">
                  {table.rowCount.toLocaleString()} {table.rowCount === 1 ? 'row' : 'rows'}
                </span>
              </div>

              {table.error && (
                <div className="mt-2 text-xs text-red-600">
                  {table.error}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Available Tables</h3>
        <p className="mt-1 text-sm text-gray-500">
          Select a table to view and manage its data
        </p>
      </div>

      {renderTableGroup('Full Access', groupedTables.full, 'green')}
      {renderTableGroup('Update Access', groupedTables.update, 'blue')}
      {renderTableGroup('Read Only', groupedTables.readonly, 'gray')}

      {tables.length === 0 && (
        <div className="text-center py-12">
          <Table className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No tables available</p>
        </div>
      )}
    </div>
  );
};

export default TableBrowser;
